import { Button } from '@creditornot/cb-button';
import { FormRow } from '@creditornot/cb-form';
import { Input } from '@creditornot/cb-input';
import { RadioCard } from '@creditornot/cb-radio';
import { Select } from '@creditornot/cb-select';
import { Modal, ModalContent, ModalFooter, ModalHeader } from '@creditornot/cbt-modal';
import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import { Field, Form } from 'react-final-form';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import * as yup from 'yup';

import { importXmenuJson } from 'apis/offer-listing-service/api';
import { CreateSelectionFromXmenuFilePayload } from 'apis/offer-listing-service/types';
import { APIErrorNotification, RFFFieldErrorWrapper } from 'common/components';
import { localize } from 'common/utils';
import validateYupSchema from 'common/utils/validateYupSchema';
import { languages } from 'config';
import { useCountryConfigContext } from 'modules/country/CountryConfigContext';
import { PartialExportedMenu } from 'modules/menu/components/ImportMenuModal/utils';
import { useCreateSharedMenuTracking } from 'modules/menu/hooks/telemetryHooks';

import { XmenuFileSelector } from './XmenuFileSelector';
import { createSharedMenu } from '../../api';
import { SHARED_MENU_NAME_MAX_LENGTH, languageToPhraseKeyMap } from '../../constants';
import { useScopeContext } from '../../contexts/ScopeContext';
import { useSharedMenuData } from '../../hooks/useSharedMenuData';
import { CreateSharedMenuPayload } from '../../types';

type CreateSelectionModalProps = {
  onClose: () => void;
  isOpen: boolean;
};

const StyledModalContent = styled(ModalContent)`
  padding: 0;
`;

const RadioCardContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px 24px 0;
`;

const StyledRadioCard = styled(RadioCard)`
  width: 100%;
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  flex-direction: column;
`;

const ModalButtons = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
`;

type FormValues = {
  name: string;
  language: Language | undefined;
};

export const validationSchema = yup.object({
  name: yup
    .string()
    .required()
    .lowercase()
    .trim()
    .max(SHARED_MENU_NAME_MAX_LENGTH)
    .test(
      'name',
      { id: 'shared-menu.validation-messages.menu.empty-name' },
      (value: string | undefined) => typeof value === 'string' && value !== '',
    )
    .when('$allSharedMenusNames', (allSharedMenusNames: string[], fieldSchema: yup.StringSchema) =>
      fieldSchema.test('name', { id: 'shared-menu.validation-messages.menu.duplicated-name' }, value =>
        typeof value === 'string' ? !allSharedMenusNames.includes(value) : true,
      ),
    ),
  language: yup
    .mixed<Language>()
    .when('$creationMode', (creationMode: 'empty-menu' | 'xmenu-import', fieldSchema: any) => {
      if (creationMode === 'empty-menu') {
        return fieldSchema.required();
      }

      return fieldSchema;
    }),
});

export const CreateSelectionModal = ({ isOpen, onClose }: CreateSelectionModalProps) => {
  const intl = useIntl();
  const scope = useScopeContext();

  const countryConfig = useCountryConfigContext();

  const [creationMode, setCreationMode] = useState<'empty-menu' | 'xmenu-import'>('empty-menu');
  const [importData, setImportData] = useState<PartialExportedMenu | undefined>(undefined);

  const { refetchSharedMenus, sharedMenus } = useSharedMenuData({ scope });

  const languageOptions = useMemo(() => {
    return languages.map(lang => ({ value: lang, label: intl.formatMessage({ id: languageToPhraseKeyMap[lang] }) }));
  }, [intl]);

  const allSharedMenusNames = sharedMenus.map(sm => localize(sm.name, sm.primary_language).toLocaleLowerCase());

  const trackMenuCreation = useCreateSharedMenuTracking();

  const {
    mutateAsync: createEmptyMenu,
    isLoading: creatingEmptyMenu,
    error: createEmptyMenuError,
    reset: resetCreateEmptyMenu,
  } = useMutation((payload: CreateSharedMenuPayload) => createSharedMenu(payload), {
    onSuccess: async data => {
      trackMenuCreation(data.id, data.primary_language);
      handleClose();
      await refetchSharedMenus();
      toast.success(
        <FormattedMessage
          id="listing-manager.create-menu.success"
          values={{
            bold: (content: any) => <strong>{content}</strong>,
            menuName: localize(data.name, data.primary_language),
          }}
        />,
      );
    },
  });

  const {
    mutateAsync: createFromXmenuFile,
    isLoading: creatingFromXmenu,
    error: createFromXmenuError,
    reset: resetCreateFromXmenuFile,
  } = useMutation(
    ({ formValues, importData }: { formValues: FormValues; importData?: PartialExportedMenu }) => {
      if (!importData || !countryConfig || !countryConfig.currency) {
        return Promise.reject();
      }

      const payload: CreateSelectionFromXmenuFilePayload = {
        currency: countryConfig.currency,
        selection_name: formValues.name,
        scope,
        menu_request: importData,
      };
      return importXmenuJson(payload);
    },
    {
      onSuccess: async (_, variables) => {
        handleClose();
        await refetchSharedMenus();
        toast.success(
          <FormattedMessage
            id="listing-manager.create-menu.success"
            values={{
              bold: (content: any) => <strong>{content}</strong>,
              menuName: variables.formValues.name,
            }}
          />,
        );
      },
      onError: () => {
        // TODO: improve & localise error handling
        toast.error(
          'Something went wrong when creating the menu! Please check that you are using a file from Xmenu and try again.',
          { duration: Infinity },
        );
      },
    },
  );

  const handleClose = useCallback(() => {
    resetCreateEmptyMenu();
    resetCreateFromXmenuFile();
    onClose();
  }, [resetCreateEmptyMenu, resetCreateFromXmenuFile, onClose]);

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      if (creationMode === 'empty-menu') {
        if (!values.language) {
          return;
        }

        const payload: CreateSharedMenuPayload = {
          name: [
            {
              value: values.name,
              lang: values.language,
              verified: true,
            },
          ],
          primary_language: values.language,
          scope,
        };
        await createEmptyMenu(payload);
      } else {
        await createFromXmenuFile({ formValues: values, importData });
      }
    },
    [creationMode, createEmptyMenu, createFromXmenuFile, importData, scope],
  );

  const error = createEmptyMenuError || createFromXmenuError;

  const validationContext = useMemo(() => ({ allSharedMenusNames, creationMode }), [allSharedMenusNames, creationMode]);

  return (
    <Modal open={isOpen} onDismiss={handleClose} sizePreset={'centered:medium'} disableContentPadding>
      <ModalHeader onCloseClick={handleClose}>
        <FormattedMessage id="listing-manager.create-menu.title" />
      </ModalHeader>
      <Form<FormValues>
        initialValues={{
          name: '',
          // TODO: fill with some default language
          language: undefined,
        }}
        onSubmit={handleSubmit}
        validate={validateYupSchema(validationSchema, validationContext)}
      >
        {({ handleSubmit, pristine, hasValidationErrors }) => (
          <form onSubmit={handleSubmit}>
            <StyledModalContent>
              <RadioCardContainer>
                <StyledRadioCard
                  checked={creationMode === 'empty-menu'}
                  onChange={() => setCreationMode('empty-menu')}
                  label={intl.formatMessage({
                    id: 'listing-manager.selections.venue-editor.create-menu.empty-menu.label',
                  })}
                  subContent={intl.formatMessage({
                    id: 'listing-manager.selections.venue-editor.create-menu.empty-menu.sub-label',
                  })}
                />
                <StyledRadioCard
                  checked={creationMode === 'xmenu-import'}
                  onChange={() => setCreationMode('xmenu-import')}
                  label={intl.formatMessage({ id: 'listing-manager.selections.venue-editor.create-menu.xmenu.label' })}
                  subContent={intl.formatMessage({
                    id: 'listing-manager.selections.venue-editor.create-menu.xmenu.sub-label',
                  })}
                />
              </RadioCardContainer>

              <FormRow label={intl.formatMessage({ id: 'shared-menu.new-shared-menu.name' })} horizontalPadding>
                <Field<FormValues['name']> name="name">
                  {field => (
                    <RFFFieldErrorWrapper field={field}>
                      {input => (
                        <Input
                          {...input}
                          placeholder={intl.formatMessage({ id: 'shared-menu.new-shared-menu.name-placeholder' })}
                        />
                      )}
                    </RFFFieldErrorWrapper>
                  )}
                </Field>
              </FormRow>

              {creationMode === 'empty-menu' && (
                <FormRow label={intl.formatMessage({ id: 'shared-menu.new-shared-menu.language' })} horizontalPadding>
                  <Field<FormValues['language']> name="language">
                    {field => (
                      <RFFFieldErrorWrapper field={field}>
                        {input => <Select {...input} options={languageOptions} />}
                      </RFFFieldErrorWrapper>
                    )}
                  </Field>
                </FormRow>
              )}

              {creationMode === 'xmenu-import' && <XmenuFileSelector setImportData={setImportData} />}
            </StyledModalContent>
            <StyledModalFooter>
              {!!error && <APIErrorNotification error={error} style={{ marginBlockEnd: '16px', width: '100%' }} />}
              <ModalButtons>
                <Button
                  variant="secondary"
                  size="small"
                  style={{ marginInlineEnd: '8px' }}
                  onClick={handleClose}
                  disabled={creatingEmptyMenu || creatingFromXmenu}
                >
                  <FormattedMessage id="state.cancel" />
                </Button>
                <Button
                  variant="primary"
                  size="small"
                  type="submit"
                  disabled={
                    pristine ||
                    hasValidationErrors ||
                    creatingEmptyMenu ||
                    creatingFromXmenu ||
                    (creationMode === 'xmenu-import' && !importData)
                  }
                  loading={creatingEmptyMenu || creatingFromXmenu}
                >
                  <FormattedMessage id="state.create" />
                </Button>
              </ModalButtons>
            </StyledModalFooter>
          </form>
        )}
      </Form>
    </Modal>
  );
};
